import { PanelState, State } from "@/shared/state/visualization";
import {
  FormulaTransform,
  PlotYAxis,
} from "@/shared/state/visualization/schema/v1";
import * as threeDSchema from "@/shared/state/visualization/schema/v1/three-d";

import { messagePathDataType, MessagePathPart } from "../topics";

export enum LayoutAccessibility {
  Organization = "organization",
  User = "user",
}

/**
 * A rule requirement that stores the topic name and the message path in
 * separate fields, so a dot inside a topic name or inside a field name is
 * never read as the boundary between the two. The message path is a list of
 * parts rather than a dotted string for the same reason.
 *
 * LayoutDefinitionBuilder writes every requirement in this shape. Only
 * `topicName` and `messagePath` decide what it matches; nothing reads
 * `topicMessagePath` or `dataType` off it. Those two restate the same binding
 * as one dotted string for a client running an older application bundle, which
 * reads only that pair, so a layout saved from one tab stays resolvable in a
 * tab that has not reloaded.
 */
export interface StructuredRequirement {
  topicName: string;
  messagePath: MessagePathPart[];
  topicMessagePath?: string;
  /** Data type of the last attribute part of `messagePath`. */
  dataType?: string;
}

/**
 * A rule requirement holding only the joined spelling: the topic name and the
 * message path in one dotted string, with no record of where the boundary
 * between them falls.
 *
 * Layouts written by an application bundle that had no split fields hold this
 * shape. Saving over such a layout rebuilds only the rules for the panels the
 * user touched, so one stored definition can hold requirements of both shapes.
 */
export interface JoinedPathRequirement {
  topicMessagePath: string;
  /**
   * Any string the writer recorded, so a layout naming a data type this
   * application does not know still loads.
   */
  dataType?: string;
  /**
   * Never set: a requirement that names a topic is read as a
   * StructuredRequirement.
   */
  topicName?: never;
}

export type Requirement = StructuredRequirement | JoinedPathRequirement;

/**
 * True when a requirement carries the split topic name and message path parts,
 * narrowing it so callers can read them.
 *
 * A `topicName` alone is not enough: `messagePath` has to be present and be an
 * array, because a requirement can arrive from a definition that was never
 * schema-checked (see the LayoutDefinition constructor). A requirement naming
 * a topic but carrying no parts array would otherwise reach the parts-based
 * matching, which expects one.
 */
export function isStructuredRequirement(
  requirement: Requirement,
): requirement is StructuredRequirement {
  return (
    "topicName" in requirement &&
    "messagePath" in requirement &&
    Array.isArray(requirement.messagePath)
  );
}

/**
 * The data type a requirement asks for; rule matching compares it against a
 * candidate's canonical data type, and a requirement that names none matches
 * on path alone.
 *
 * A requirement carrying `messagePath` parts takes it from the last attribute
 * part rather than from its stored `dataType`, so the stored copy cannot
 * disagree with the parts. A requirement holding only the joined dotted string
 * has nothing but the stored `dataType`.
 */
export function requirementDataType(
  requirement: Requirement,
): string | undefined {
  if (isStructuredRequirement(requirement)) {
    return messagePathDataType(requirement.messagePath);
  }
  return requirement.dataType;
}

/**
 * Binds workspace topics to panel data. Panels list the rules they draw by id,
 * and one rule can be listed by several panels.
 */
export interface Rule {
  id: string;

  /**
   * Every requirement has to match for the rule to bind, and each match holds
   * one topic per requirement in this order. A panel that draws several topics
   * as a unit (a Map path's latitude and longitude, a 3D entity's forward,
   * right, and up) addresses them by their index here.
   */
  requirements: Requirement[];

  /** Styles to apply to the panel when this rule matches data */
  style?: RuleStyle;
}

export type RuleStyle = Partial<{
  /** Color (HSLA, hex, or RGBA) to apply to the matched data */
  color: string;

  /** Line widths for plotted data */
  lineWidth: number;
  /** Point sizes for plotted data */
  pointRadius: number;
  /** Y axis for plotted data */
  yAxis: PlotYAxis;
  /** Label for plotted data */
  label: string;
  /** Transform for plotted data */
  transform: FormulaTransform;

  threeDComponents: {
    entityId: threeDSchema.EntityID;
    position?: ThreeDPositionStyle;
    rotation?: threeDSchema.Rotation;
    mesh?: threeDSchema.Mesh;
    path?: threeDSchema.Path;
    vectorField?: threeDSchema.VectorField;
    pointCloud?: threeDSchema.PointCloud;
    /**
     * Where `pointCloud.source` sits in the rule's requirements, as a
     * position axis's `topicDataIndex` says where its topic sits. Opening the
     * layout binds the cloud to the topic matched there, so a layout saved on
     * one recording draws the cloud of whichever recording it is opened on.
     * Absent from a rule saved before it was written, whose cloud keeps the
     * `source` it was saved with.
     */
    pointCloudTopicDataIndex?: number;
  };
}>;

/**
 * The styling & data mapping for a 3D position.
 *
 * This defines the topic data slots, along with the coordinate transformation & config presets.
 */
export interface ThreeDPositionStyle {
  transformation?: threeDSchema.PositionTransformation;
  basePreset?: threeDSchema.PositionBasePreset;
  forward: ThreeDPositionDataMapping;
  right: ThreeDPositionDataMapping;
  up: ThreeDPositionDataMapping;
}

/**
 * Where one axis of a 3D entity's position comes from.
 *
 * A `fixed` axis holds the constant `value`. A `topicData` axis reads a topic
 * the rule matched, picked by its position in the rule's requirement list
 * (`topicDataIndex`). Either way the panel multiplies the axis by `scale`
 * before placing the entity, so a negative scale points the axis the other
 * way.
 */
export type ThreeDPositionDataMapping =
  | { type: "topicData"; scale: number; topicDataIndex: number }
  | { type: "fixed"; scale: number; value: number };

export type LayoutPanelState = Omit<PanelState, "data"> & {
  rules: string[];
};

/**
 * The saved shape of a layout: the panels keyed by panel id, the split tree
 * that arranges them, and the rules the panels draw.
 *
 * It holds no topic data. Each panel's data is rebuilt when the layout is
 * opened, by matching the rules it lists against the topics of the files in
 * the workspace, so the same layout can be opened on other files.
 */
export interface LayoutDefinition {
  panels: Record<string, LayoutPanelState>;
  layout: State["layout"];
  rules: Rule[];
}

export interface LayoutRecord {
  accessibility: LayoutAccessibility;
  created: string;
  created_by: string;
  /** Name of the folder this layout is grouped under; null/undefined means ungrouped (root). */
  folder?: string | null;
  layout_definition: LayoutDefinition;
  layout_id: string;
  modified: string;
  modified_by: string;
  name: string;
  org_id: string;
  schema_version: number;
  tags: string[];
}

export interface CreateLayoutRequest {
  accessibility: LayoutAccessibility;
  folder?: string | null;
  layout_definition: LayoutDefinition;
  name: string;
  schema_version: number;
  tags: string[];
}

export type UpdateLayoutRequest = Partial<{
  accessibility: LayoutAccessibility;
  /** A folder name moves the layout into that folder; null moves it to the root. */
  folder: string | null;
  layout_definition: LayoutDefinition;
  name: string;
  schema_version: number;
  tags: string[];
}>;

/**
 * Narrows an entry of the `conflicting_layouts` array carried on a 409
 * layout-conflict error body, which reaches this code as `unknown`.
 *
 * Only presence is checked, never a field's type or contents: the conflict
 * flow reads `name` and `accessibility` to tell the user's layout from the
 * shared one and `layout_id` to rename it, and nothing there reads the stored
 * definition.
 */
export function isLayoutRecord(value: unknown): value is LayoutRecord {
  return (
    value instanceof Object &&
    "layout_id" in value &&
    "name" in value &&
    "accessibility" in value &&
    "layout_definition" in value &&
    "schema_version" in value
  );
}
